import { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * usePushSubscription
 * --------------------
 * One hook that handles the full web-push handshake:
 *
 *   1. Detect support (Notification + ServiceWorker + PushManager).
 *   2. Fetch the org's VAPID public key from /api/push/config.
 *   3. If the user already granted permission, ensure a subscription
 *      exists (re-subscribe silently if the browser rotated keys).
 *   4. Expose `subscribe()` so we can prompt explicitly from a banner.
 *   5. Expose `disable()` for the settings toggle.
 *
 * Notes for the consumer:
 *   - `state` cycles through: 'unsupported' | 'idle' | 'prompt' | 'granted'
 *     | 'denied' | 'error'.
 *   - The hook never auto-prompts — it only re-uses a granted permission.
 *     The compose UX shows a one-time gentle banner that calls subscribe().
 */

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const supported = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

export function usePushSubscription({ enabled = true } = {}) {
  const [state, setState] = useState(supported() ? 'idle' : 'unsupported');
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState(null);

  const ensureSubscribed = useCallback(async ({ requestPermission = false } = {}) => {
    if (!supported()) { setState('unsupported'); return null; }
    setError('');

    let perm = Notification.permission;
    if (perm === 'default' && requestPermission) {
      perm = await Notification.requestPermission();
    }
    if (perm === 'denied') { setState('denied'); return null; }
    if (perm !== 'granted') { setState('prompt'); return null; }

    try {
      const cfg = await api.getPushConfig();
      if (!cfg?.enabled || !cfg?.vapid_public_key) {
        setState('error');
        setError('Push is not enabled on the server.');
        return null;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      // If we already have a sub but the key rotated, drop it.
      const expected = cfg.vapid_public_key;
      if (sub) {
        const existingKey =
          sub.options && sub.options.applicationServerKey
            ? new Uint8Array(sub.options.applicationServerKey)
            : null;
        const desired = urlBase64ToUint8Array(expected);
        const matches =
          existingKey &&
          existingKey.length === desired.length &&
          existingKey.every((b, i) => b === desired[i]);
        if (!matches) {
          await sub.unsubscribe().catch(() => {});
          sub = null;
        }
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(expected),
        });
      }
      // Ship it to the backend (idempotent).
      await api.pushSubscribe(sub.toJSON());
      setSubscription(sub);
      setState('granted');
      return sub;
    } catch (e) {
      setState('error');
      setError(e.message || String(e));
      return null;
    }
  }, []);

  const subscribe = useCallback(() => ensureSubscribed({ requestPermission: true }), [ensureSubscribed]);

  const disable = useCallback(async () => {
    if (!supported()) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscription(null);
      setState('idle');
    } catch (e) {
      setError(e.message || String(e));
    }
  }, []);

  // Silent re-attach when permission was already granted.
  useEffect(() => {
    if (!enabled || !supported()) return;
    if (Notification.permission === 'granted') {
      ensureSubscribed({ requestPermission: false });
    } else if (Notification.permission === 'denied') {
      setState('denied');
    } else {
      setState('prompt');
    }
  }, [enabled, ensureSubscribed]);

  return { state, error, subscription, subscribe, disable };
}

export default usePushSubscription;
